import { useCallback, useId, useRef, useState, type ChangeEvent, type DragEvent } from 'react';

import { formatFileSize } from '@/lib/format';

/** 3 ГиБ — лимит до отправки на сервер. */
export const MAX_FILE_SIZE_BYTES = 3_221_225_472;
export const MAX_FILE_SIZE_LABEL = 'До 3 ГБ на файл';
export const MAX_TOTAL_SIZE_BYTES = 9_663_676_416; // 9 ГиБ суммарно
export const MAX_TOTAL_SIZE_LABEL = 'До 9 ГБ суммарно';
export const MAX_PARTS_COUNT = 100;

export type UploadDropzoneProps = {
  files: File[];
  disabled?: boolean;
  error?: string | null;
  onFilesChange: (files: File[]) => void;
};

export function UploadDropzone({
  files,
  disabled = false,
  error = null,
  onFilesChange,
}: UploadDropzoneProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const pickFiles = useCallback(
    (nextFiles: File[]) => {
      onFilesChange(nextFiles);
      if (inputRef.current !== null && nextFiles.length === 0) {
        inputRef.current.value = '';
      }
    },
    [onFilesChange],
  );

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextFiles = Array.from(event.target.files ?? []);
    pickFiles(nextFiles);
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (disabled) {
      return;
    }
    setDragging(true);
  };

  const onDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (disabled) {
      return;
    }
    const nextFiles = Array.from(event.dataTransfer.files ?? []);
    pickFiles(nextFiles);
  };

  const removeFile = (index: number) => {
    const nextFiles = files.filter((_, i) => i !== index);
    pickFiles(nextFiles);
  };

  const moveFileUp = (index: number) => {
    if (index === 0 || index >= files.length) {
      return;
    }
    const prevFile = files[index - 1];
    const currentFile = files[index];
    if (!prevFile || !currentFile) {
      return;
    }
    const nextFiles = [...files];
    nextFiles[index - 1] = currentFile;
    nextFiles[index] = prevFile;
    pickFiles(nextFiles);
  };

  const moveFileDown = (index: number) => {
    if (index < 0 || index >= files.length - 1) {
      return;
    }
    const currentFile = files[index];
    const nextFile = files[index + 1];
    if (!currentFile || !nextFile) {
      return;
    }
    const nextFiles = [...files];
    nextFiles[index] = nextFile;
    nextFiles[index + 1] = currentFile;
    pickFiles(nextFiles);
  };

  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  const hasFiles = files.length > 0;
  const isMultipart = files.length > 1;

  const className = [
    'dropzone',
    dragging ? 'dropzone--active' : null,
    hasFiles ? 'dropzone--has-file' : null,
    disabled ? 'dropzone--disabled' : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={className}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      aria-disabled={disabled}
    >
      <div className="dropzone__icon" aria-hidden="true">
        ↑
      </div>
      <p className="dropzone__title">
        {hasFiles ? (isMultipart ? 'Файлы выбраны' : 'Файл выбран') : 'Перетащите файл или файлы в эту область'}
      </p>
      <p className="dropzone__description">
        {isMultipart
          ? 'Несколько последовательных частей одной встречи. Проверьте порядок и измените при необходимости.'
          : 'Один файл аудио или видео, либо несколько последовательных частей одной встречи. Тип и пригодность проверяются на сервере.'}
      </p>

      {hasFiles ? (
        <>
          {isMultipart ? (
            <div className="dropzone__multipart-hint" role="status">
              <p>
                <strong>Многочастная встреча:</strong> {files.length} {files.length === 1 ? 'часть' : files.length < 5 ? 'части' : 'частей'}
              </p>
              <p>Убедитесь, что части расположены в правильном хронологическом порядке.</p>
            </div>
          ) : null}

          <ul className="dropzone__files" data-testid="selected-files">
            {files.map((file, index) => (
              <li key={`${file.name}-${index}`} className="dropzone__file-item">
                <div className="dropzone__file-info">
                  {isMultipart ? (
                    <span className="dropzone__file-index">#{index + 1}</span>
                  ) : null}
                  <span className="dropzone__file-name">{file.name}</span>
                  <span className="dropzone__file-size">{formatFileSize(String(file.size))}</span>
                </div>
                <div className="dropzone__file-actions">
                  {isMultipart ? (
                    <>
                      <button
                        type="button"
                        className="button button--ghost button--sm"
                        onClick={() => moveFileUp(index)}
                        disabled={disabled || index === 0}
                        aria-label={`Переместить ${file.name} выше`}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="button button--ghost button--sm"
                        onClick={() => moveFileDown(index)}
                        disabled={disabled || index === files.length - 1}
                        aria-label={`Переместить ${file.name} ниже`}
                      >
                        ↓
                      </button>
                    </>
                  ) : null}
                  <button
                    type="button"
                    className="button button--ghost button--sm"
                    onClick={() => removeFile(index)}
                    disabled={disabled}
                    aria-label={`Удалить ${file.name}`}
                  >
                    ×
                  </button>
                </div>
              </li>
            ))}
          </ul>

          {isMultipart ? (
            <p className="dropzone__total">
              Суммарный размер: <strong>{formatFileSize(String(totalSize))}</strong>
            </p>
          ) : null}
        </>
      ) : null}

      <div className="dropzone__limits">
        <span className="dropzone__limit">{MAX_FILE_SIZE_LABEL}</span>
        {isMultipart ? (
          <span className="dropzone__limit">{MAX_TOTAL_SIZE_LABEL}</span>
        ) : null}
        <span className="dropzone__limit">{isMultipart ? 'Части одной встречи' : 'Один файл или несколько частей'}</span>
        <span className="dropzone__limit">Пригодность — на сервере</span>
      </div>

      <div className="dropzone__actions">
        <label className="button button--secondary button--md" htmlFor={inputId}>
          {hasFiles ? 'Выбрать другие файлы' : 'Выбрать файл'}
        </label>
        {hasFiles && !disabled ? (
          <button
            type="button"
            className="button button--ghost button--md"
            onClick={() => {
              pickFiles([]);
            }}
          >
            Очистить
          </button>
        ) : null}
      </div>

      <input
        ref={inputRef}
        id={inputId}
        className="visually-hidden"
        type="file"
        multiple
        disabled={disabled}
        onChange={onInputChange}
      />

      {error !== null && error !== '' ? (
        <p className="field__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
